import React, { useMemo } from 'react';
import {
  EMPTY, fmtInt, fmtNum, fmtDate, fmtDateTime, fmtRelative, fmtDistance, fmtDays,
} from '../../utils/statsFormat';
import './SiteSection.css';

/**
 * Section A of the Statistics page — the receiver's identity and headline
 * numbers, in two explicitly-labelled scopes: Today and All time.
 *
 * This replaces three separate renderings of the same figures:
 *   - ReceiverDetail's five `.stat-card`s (all-time / today / new / messages)
 *   - the numeric rows previously mixed into Statistics' site-information grid
 *   - (the map HUDs keep their own copies — those are map chrome, not this page)
 *
 * Two sources, deliberately:
 *   - `overview`  — getReceiverSiteOverview: identity + all-time rollups.
 *   - `todayStats`— the periodic `receiverStatistics` websocket broadcast, the
 *                   same payload the HUDs read. Reusing it keeps today's numbers
 *                   identical across the app and costs no extra request.
 *
 * Missing values render as an em-dash, never 0 — the websocket payload arrives
 * a beat after the page does, and "0 aircraft today" is a different claim from
 * "we haven't heard yet".
 */

/** One headline figure. `hint` is optional supporting context under the label. */
function StatTile({ value, unit, label, hint, wide = false }) {
  return (
    <div className={`site-tile${wide ? ' site-tile-wide' : ''}`}>
      <div className="site-tile-value">
        {value}
        {unit && value !== EMPTY && <span className="site-tile-unit">{unit}</span>}
      </div>
      <div className="site-tile-label">{label}</div>
      {hint && <div className="site-tile-hint">{hint}</div>}
    </div>
  );
}

/** A labelled row of tiles — the scope label is what makes the numbers legible. */
function TileGroup({ title, children }) {
  return (
    <div className="site-tile-group">
      <div className="site-tile-group-title">{title}</div>
      <div className="site-tiles">{children}</div>
    </div>
  );
}

function InfoRow({ label, value, sub, title }) {
  return (
    <div className="site-info-item">
      <span className="site-info-label">{label}</span>
      <span className="site-info-value" title={title}>{value}</span>
      {sub && <span className="site-info-sub">{sub}</span>}
    </div>
  );
}

/**
 * Combined live message rate across the receiver's streams. The DTO carries
 * beast/acars/other separately; the tile shows the total and names the split so
 * a VDL2-only site doesn't look like it's reporting ADS-B throughput.
 */
function messageRate(todayStats) {
  if (!todayStats) return { total: null, hint: null };
  const parts = [
    ['ADS-B', todayStats.beastMsgRate],
    ['ACARS', todayStats.acarsMsgRate],
    ['other', todayStats.otherMsgRate],
  ].filter(([, v]) => v != null && Number(v) > 0);

  if (parts.length === 0) return { total: null, hint: null };
  const total = parts.reduce((sum, [, v]) => sum + Number(v), 0);
  const hint = parts.length > 1
    ? parts.map(([name, v]) => `${name} ${fmtNum(v, 1)}`).join(' · ')
    : null;
  return { total, hint };
}

function SiteSection({ overview, todayStats, unitSystem = 'metric' }) {
  const rate = useMemo(() => messageRate(todayStats), [todayStats]);

  const peakRange = fmtDistance(overview?.peakRangeKm, unitSystem, 0);

  const location = (overview?.latitude != null && overview?.longitude != null)
    ? `${overview.latitude.toFixed(3)}, ${overview.longitude.toFixed(3)}`
    : EMPTY;

  const longestStreak = overview?.longestStreakDays > 0
    ? fmtDays(overview.longestStreakDays)
    : EMPTY;
  const longestStreakSpan = overview?.longestStreakStart
    ? `${overview.longestStreakStart} → ${overview.longestStreakEnd}`
    : null;

  const busiestDay = overview?.busiestDayAircraft > 0
    ? `${fmtInt(overview.busiestDayAircraft)} aircraft`
    : EMPTY;
  const busiestDayWhen = overview?.busiestDayDate || null;

  return (
    <section className="site-section" id="site">
      <header className="stats-section-header">
        <h2 className="stats-section-heading">Site</h2>
        {overview?.receiverName && (
          <span className="stats-section-sub">{overview.receiverName}</span>
        )}
      </header>

      <TileGroup title="Today">
        <StatTile
          value={fmtInt(todayStats?.totalUniqueAircraftToday)}
          label="Aircraft"
        />
        <StatTile
          value={fmtInt(todayStats?.newAircraftToday)}
          label="New aircraft"
          hint="first ever seen here"
        />
        <StatTile
          value={fmtInt(todayStats?.totalMessagesToday)}
          label="Messages"
        />
        <StatTile
          value={rate.total == null ? EMPTY : fmtNum(rate.total, 1)}
          unit="/s"
          label="Message rate"
          hint={rate.hint}
        />
      </TileGroup>

      <TileGroup title="All time">
        <StatTile
          value={fmtInt(overview?.totalUniqueAircraft)}
          label="Aircraft"
        />
        <StatTile
          value={fmtInt(overview?.totalMessages)}
          label="Messages"
        />
        <StatTile
          value={fmtInt(overview?.totalPositions)}
          label="Positions"
        />
        <StatTile
          value={peakRange.value}
          unit={peakRange.unit}
          label="Peak range"
        />
        <StatTile
          value={overview?.activeDays > 0 ? fmtInt(overview.activeDays) : EMPTY}
          label="Active days"
          hint={overview?.firstMessageAt ? `since ${fmtDate(overview.firstMessageAt)}` : null}
        />
      </TileGroup>

      <div className="site-info-card">
        <div className="site-info-header">Site information</div>
        <div className="site-info-grid">
          <InfoRow label="Nearest airport" value={overview?.airportIcao || EMPTY} />
          <InfoRow label="Location" value={location} />
          <InfoRow label="Timezone" value={overview?.timezone || EMPTY} />
          <InfoRow label="Receiver type" value={overview?.receiverType || EMPTY} />
          <InfoRow label="Status" value={overview?.status || EMPTY} />
          <InfoRow
            label="Last active"
            value={fmtRelative(overview?.lastActiveAt)}
            title={overview?.lastActiveAt ? fmtDateTime(overview.lastActiveAt) : undefined}
          />
          <InfoRow label="Joined" value={fmtDate(overview?.joinedAt)} />
          <InfoRow label="First seen" value={fmtDate(overview?.firstMessageAt)} />
          <InfoRow
            label="Current streak"
            value={overview?.currentStreakDays > 0 ? fmtDays(overview.currentStreakDays) : EMPTY}
          />
          <InfoRow
            label="Longest streak"
            value={longestStreak}
            sub={longestStreakSpan}
          />
          <InfoRow
            label="Busiest day"
            value={busiestDay}
            sub={busiestDayWhen}
          />
        </div>
      </div>
    </section>
  );
}

export default SiteSection;
